
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { fetchWithRetry } from "./fetchWithRetry";

/**
 * Handles removing a routine from a personal plan,
 * which may include completely deleting the routine if owned by the user
 */
export const handleRemoveFromPersonalPlan = async (
  routineId: string, 
  planId: string, 
  userId: string
) => {
  // First unlink the routine from the plan
  const { error: unlinkError } = await fetchWithRetry(async () => {
    return supabase
      .from("workout_plan_routines")
      .delete()
      .eq("workout_plan_id", planId)
      .eq("routine_id", routineId);
  });
  
  if (unlinkError) throw unlinkError;
  
  // Check if the current user owns this routine
  const { data: routineInfo, error: routineError } = await supabase
    .from("routines")
    .select("user_id")
    .eq("id", routineId)
    .single();
    
  if (routineError) {
    console.error("Error fetching routine info:", routineError);
    throw routineError;
  }
  
  // If user owns the routine, fully delete it since it's a personal copy
  if (routineInfo.user_id === userId) {
    await deleteRoutineCompletely(routineId);
    
    toast({
      title: "Rutina eliminada",
      description: "La rutina se ha eliminado completamente",
      variant: "success"
    });
  } else {
    toast({
      title: "Rutina eliminada",
      description: "La rutina ha sido eliminada de tu plan personal",
      variant: "success"
    });
  }
};

/**
 * Handles removing a routine from a shared plan by just unlinking it
 */
export const handleRemoveFromSharedPlan = async (routineId: string, planId: string) => {
  // For non-personal plans, proceed with the standard removal logic
  const { error: unlinkError } = await fetchWithRetry(async () => {
    return supabase
      .from("workout_plan_routines")
      .delete()
      .eq("workout_plan_id", planId)
      .eq("routine_id", routineId);
  });

  if (unlinkError) throw unlinkError;

  toast({
    title: "Rutina desvinculada",
    description: "La rutina se ha eliminado del plan",
    variant: "success"
  });
};

/**
 * Helper function to completely delete a routine and its related data
 */
const deleteRoutineCompletely = async (routineId: string) => {
  // Delete exercises first
  const { error: exercisesError } = await supabase
    .from("exercises")
    .delete()
    .eq("routine_id", routineId);

  if (exercisesError) {
    console.error("Error removing exercises:", exercisesError);
    throw exercisesError;
  }

  // Delete routine days
  const { error: daysError } = await supabase
    .from("routine_days")
    .delete()
    .eq("routine_id", routineId);

  if (daysError) {
    console.error("Error removing routine days:", daysError);
    throw daysError;
  }

  // Finally delete the routine
  const { error: deleteError } = await supabase
    .from("routines")
    .delete()
    .eq("id", routineId);

  if (deleteError) {
    console.error("Error removing routine:", deleteError);
    throw deleteError;
  }
};
